import { useContext, useEffect, useRef, useState } from "react";
import CloseIcon from "../../assets/close.svg?react";
import DownloadIcon from "../../assets/download.svg?react";

import { useDispatch, useSelector } from "react-redux";
import { LeftPanelTabs, RightPanelTabs } from "../../constants/Panels";
import { AppContext, UserContext } from "../../Context";
import { changeRightPanelTab, setLeftPanelActiveTab } from "../../store/appSlice";
import { StoreState } from "../../store/rootReducer";
import { exportFeatureToShape, getFeatureFromGlobalId, highlightFeature } from "../../utils/arcgisUtils";
import { openInFDV } from "../../utils/FDVUtils";
import { onRefreshFeature } from "../../utils/helpers";
import { canEditVersion } from "../../utils/versioningUtils";
import { canHaveSectors } from "../../utils/lighthouseUtils";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import "./FeatureViewer.css";
import FeatureViewerContent from "./FeatureViewerContent";
import ScreeningReportModal from "./ScreeningReportModal/ScreeningReportModal";
import { AppDispatch } from "../../store/storeSetup";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

const FeatureViewer = () => {
    const dispatch = useDispatch<AppDispatch>();

    const [canIEditVersion, setCanIEditVersion] = useState<boolean>(false);
    const [canBeSentToFdv, setCanBeSentToFdv] = useState<boolean>(false);
    const [isScreenReportModalOpen, setIsScreenReportModalOpen] = useState<boolean>(false);
    const [selectedWorkOrderIds, setSelectedWorkOrderIds] = useState<string[]>([]);

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    const versionsSharedWithMe = useSelector((state: StoreState) => state.version.versionsSharedWithMe);

    const leftPanelActiveTab = useSelector((state: StoreState) => state.app.leftPanelActiveTab);

    const shownFeatureHighlightRef = useRef<ResourceHandle | null>(null);

    const appContext = useContext(AppContext);
    const userContext = useContext(UserContext);
    const shownFeature = appContext?.shownFeature.value;
    const setShownFeature = appContext!.shownFeature.set;
    const setSelectedFeature = appContext!.selectedFeature.set;

    const unshowFeature = () => {
        dispatch(setLeftPanelActiveTab(LeftPanelTabs.UNDEFINED));
        setShownFeature(null);
    };

    const exportCableAsShape = () => {
        if (!shownFeature) return;
        const email = userContext?.user.value?.email ?? "";
        const fileName =
            "sjøkabel_" +
            (shownFeature.attributes.navn ?? shownFeature.attributes.objectid) +
            "_" +
            Date.now().toLocaleString() +
            ".shp";

        exportFeatureToShape(shownFeature, fileName, email);
    };

    const openSeaCableEditing = () => {
        if (shownFeature) {
            dispatch(changeRightPanelTab(RightPanelTabs.SEA_CABLE));
        }
    };

    const canEditFeature = () => {
        if (!shownFeature) return false;
        if (!activeVersion) return false;
        if ((shownFeature.layer as FeatureLayer).layerId === 34) {
            return false;
        }
        return true;
    };

    const setObjectAsEditingObject = async () => {
        const result = dispatch(changeRightPanelTab(RightPanelTabs.EDITOR));
        if (!result || !shownFeature) {
            // User cancelled, do not proceed with editing
            return;
        }
        appContext?.selectedFeatureHighlightRef.value.current?.remove();

        const layer = shownFeature.layer as FeatureLayer;
        const globalId = shownFeature.attributes?.globalid;
        if (layer.layerId && globalId) {
            const freshFeature = await getFeatureFromGlobalId(layer.layerId, globalId);
            setSelectedFeature(freshFeature ?? shownFeature);
        } else {
            setSelectedFeature(shownFeature);
        }

        if (shownFeature.layer) {
            shownFeature.layer.visible = true;
        }
    };

    useEffect(() => {
        if (activeVersion) {
            const user = userContext?.user.value;
            canEditVersion(activeVersion, versionsSharedWithMe ?? [], user).then((result) => {
                setCanIEditVersion(result);
            });
        }
    }, [activeVersion, versionsSharedWithMe]);

    useEffect(() => {
        setSelectedWorkOrderIds([]);
        if (shownFeature) {
            if (shownFeatureHighlightRef.current) {
                shownFeatureHighlightRef.current.remove();
            }
            highlightFeature(shownFeature, "temporary").then((handle) => {
                if (handle) {
                    shownFeatureHighlightRef.current = handle;
                }
            });
        }
    }, [shownFeature]);

    useEffect(() => {
        if (leftPanelActiveTab !== LeftPanelTabs.VIEWER) {
            appContext?.shownFeature.set(null);
        }
    }, [leftPanelActiveTab]);

    useEffect(() => {
        return () => {
            if (shownFeatureHighlightRef?.current) {
                shownFeatureHighlightRef.current.remove();
            }
        };
    }, []);

    // Listen for refreshFeature events
    useEffect(() => {
        const removeListener = onRefreshFeature((layerId, objectId) => {
            if (!shownFeature) return;

            const layer = shownFeature.layer as FeatureLayer;
            const currentLayerId = layer?.layerId?.toString();
            const currentObjectId = shownFeature.attributes?.objectid || shownFeature.attributes?.OBJECTID;

            // Check if the event matches the currently shown feature
            if (currentLayerId === layerId && currentObjectId === objectId) {
                const globalId = shownFeature.attributes?.globalid || shownFeature.attributes?.GlobalID;
                if (globalId && layer?.layerId !== undefined && layer?.layerId !== null) {
                    // Fetch the fresh feature using globalId
                    getFeatureFromGlobalId(layer.layerId, globalId).then((freshFeature) => {
                        if (freshFeature) {
                            setShownFeature(freshFeature);
                        }
                    });
                }
            }
        });

        return removeListener;
    }, [shownFeature, setShownFeature]);

    if (!shownFeature) {
        return null;
    }

    return (
        <div className={`feature-viewer`}>
            <div className="feature-viewer-header" style={{ paddingBottom: 0 }}>
                <span className="flex-row small-gap w-100"></span>
                <button type="button" className="empty-button" onClick={unshowFeature}>
                    <CloseIcon />
                </button>
            </div>
            <FeatureViewerContent
                canBeSentToFdv={canBeSentToFdv}
                setCanBeSentToFdv={setCanBeSentToFdv}
                isEditing={false}
                feature={shownFeature}
                canMove={false}
                setHasEditedAttributes={() => {}}
            />
            <div className="feature-viewer-footer">
                {canIEditVersion && canEditFeature() && (
                    <NfsButton onClick={setObjectAsEditingObject}>Redigér</NfsButton>
                )}
                {shownFeature?.attributes["gis_obj_id"] && shownFeature?.attributes["gis_obj_id"] > 0 && (
                    <NfsButton
                        onClick={() => {
                            openInFDV(shownFeature.attributes["gis_obj_id"]);
                        }}
                    >
                        Åpne objektet i FDV
                    </NfsButton>
                )}
                {canHaveSectors(shownFeature) && (
                    <NfsButton onClick={() => setIsScreenReportModalOpen(true)}>Skjermingsrapport</NfsButton>
                )}
                {shownFeature.layer?.id === "Sjøkabel" && (
                    <div className="flex-row small-gap w-100">
                        <button className="feature-viewer-tool flex-row" onClick={exportCableAsShape}>
                            <DownloadIcon />
                            <span>Eksporter sjøkabel</span>
                        </button>
                        <button className="feature-viewer-tool" onClick={openSeaCableEditing}>
                            <span>Se dybdeprofil</span>
                        </button>
                    </div>
                )}
            </div>
            <ScreeningReportModal
                isScreenReportModalOpen={isScreenReportModalOpen}
                closeModal={() => setIsScreenReportModalOpen(false)}
                selectedFeature={shownFeature}
                selectedWorkOrderIds={selectedWorkOrderIds}
                setSelectedWorkOrderIds={setSelectedWorkOrderIds}
            />
        </div>
    );
};

export default FeatureViewer;
